import { ThemeEvents } from '@theme/events';
import {
  countryFilterOptionsFromCodes,
  defaultCountryFilterOptions,
  renderCountryFilterChips,
  resolveCountryFilters,
} from './store-location-filters.js';
import {
  normalizeStore,
  parseStoreApiResponse,
} from './store-location-data.js';
import {
  applyStorePagination,
  createStoreDrawer,
  renderStoreResults,
} from './store-location-ui.js';

/**
 * PDP store finder: fetches stores for current variant SKU via app proxy.
 */

/** @typedef {import('./store-location-data.js').StoreMeta} StoreMeta */

const SEARCH_DEBOUNCE_MS = 600;

/** @type {import('./store-location-ui.js').StoreLocationUiConfig} */
const UI_CONFIG = {
  bemPrefix: 'store-finder',
  hoursPrefix: 'store-finder',
  detailIdPrefix: 'ProductStoreFinderDetail',
  panelIdPrefix: 'ProductStoreFinderPanel',
  panelTitleIdPrefix: 'ProductStoreFinderPanelTitle',
  mapLinkClass: 'store-finder__card-link',
  mapIconWrapperClass: 'store-finder__card-link-icon',
  panelMapLinkClass: 'store-finder__card-link store-finder__panel-map-link',
  panelMapIconClass: 'store-finder__card-link-icon',
  mapIconInSpan: false,
  openClass: 'store-finder--drawer-open',
  scrollDatasetKey: 'productStoreFinderScrollY',
  templatesSelector: '[data-product-store-finder-templates]',
};

class ProductStoreFinder extends HTMLElement {
  /** @type {ReturnType<typeof createStoreDrawer> | null} */
  #drawer = null;
  /** @type {HTMLElement | null} */
  #grid = null;
  /** @type {HTMLElement | null} */
  #statusEl = null;
  /** @type {HTMLElement | null} */
  #emptyEl = null;
  /** @type {HTMLElement | null} */
  #paginationNav = null;
  /** @type {HTMLElement | null} */
  #loadingEl = null;
  /** @type {HTMLInputElement | null} */
  #search = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #searchDebounce = null;
  /** @type {string} */
  #searchQuery = '';
  /** @type {HTMLElement | null} */
  #overlay = null;
  /** @type {HTMLElement | null} */
  #panel = null;
  /** @type {HTMLElement | null} */
  #panelBody = null;
  /** @type {HTMLButtonElement | null} */
  #closeBtn = null;
  /** @type {string | undefined} */
  #sectionId;
  /** @type {AbortController | null} */
  #fetchAbort = null;
  /** @type {Record<string, string>} */
  #countryLabels = {};
  /** @type {string} */
  #mapIconHtml = '';
  /** @type {string} */
  #labelShowOnMap = '';
  /** @type {string} */
  #labelError = '';
  /** @type {string} */
  #labelEmpty = '';
  /** @type {string} */
  #labelDeliveryHours = '';
  /** @type {string} */
  #labelOpeningHours = '';
  /** @type {string} */
  #sku = '';
  /** @type {string} */
  #countryCode = 'all';
  /** @type {number} */
  #page = 1;
  /** @type {StoreMeta | null} */
  #lastMeta = null;
  /** @type {boolean} */
  #scrollAfterPageChange = false;
  /** @type {string} */
  #labelCountryAll = '';
  /** @type {HTMLElement | null} */
  #filtersContainer = null;
  /** @type {import('./store-location-filters.js').CountryFilterOption[]} */
  #backendCountries = [];
  /** @type {boolean} */
  #countryFiltersFromApi = false;
  /** @type {boolean} */
  #clientSearchMode = false;
  /** @type {unknown[] | null} */
  #clientFilteredCache = null;
  /** @type {string} */
  #clientCacheKey = '';
  /** @type {number} */
  #clientPerPage = 20;
  /** @type {unknown[] | null} */
  #allStoresRawCache = null;
  /** @type {string} */
  #allStoresRawCacheKey = '';

  /** @param {KeyboardEvent} e */
  #onKeydown = (e) => {
    if (e.key !== 'Escape' || !this.#drawer?.isOpen()) return;
    e.preventDefault();
    this.#drawer.close();
  };

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    if (!(event instanceof CustomEvent) || !event.detail) return;

    const productId = String(this.dataset.productId || '');
    const eventProductId = String(event.detail.data?.productId || '');
    if (productId && eventProductId && productId !== eventProductId) return;

    const sku = event.detail.resource?.sku || '';
    this.#setSku(sku);
  };

  connectedCallback() {
    if (this.dataset.designMode === 'true') return;

    this.#grid = this.querySelector('[data-product-store-finder-grid]');
    this.#statusEl = this.querySelector('[data-product-store-finder-status]');
    this.#emptyEl = this.querySelector('[data-product-store-finder-empty]');
    this.#paginationNav = this.querySelector('[data-product-store-finder-pagination]');
    this.#loadingEl = this.querySelector('[data-product-store-finder-loading]');
    this.#search = this.querySelector('[data-product-store-finder-search]');
    this.#overlay = this.querySelector('[data-product-store-finder-overlay]');
    this.#panel = this.querySelector('[data-product-store-finder-panel]');
    this.#panelBody = this.querySelector('[data-product-store-finder-panel-body]');
    this.#closeBtn = this.querySelector('[data-product-store-finder-close]');
    this.#sectionId = this.dataset.sectionId;

    this.#labelShowOnMap = this.dataset.labelShowOnMap || '';
    this.#labelError = this.dataset.labelError || '';
    this.#labelEmpty = this.dataset.labelEmpty || '';
    this.#labelDeliveryHours = this.dataset.labelDeliveryHours || '';
    this.#labelOpeningHours = this.dataset.labelOpeningHours || '';
    this.#labelCountryAll = this.dataset.labelCountryAll || 'All';
    this.#filtersContainer = this.querySelector('[data-product-store-finder-filters]');
    this.#sku = (this.dataset.initialSku || '').trim();

    const labelsScript = this.querySelector('[data-product-store-finder-country-labels]');
    if (labelsScript?.textContent) {
      try {
        this.#countryLabels = JSON.parse(labelsScript.textContent);
      } catch {
        this.#countryLabels = {};
      }
    }

    this.#backendCountries = defaultCountryFilterOptions(this.#countryLabels);

    const iconTpl = this.querySelector('[data-product-store-finder-templates] template');
    if (iconTpl?.innerHTML) {
      this.#mapIconHtml = iconTpl.innerHTML.trim();
    }

    if (this.#panel && this.#overlay && this.#panelBody) {
      this.#drawer = createStoreDrawer({
        root: this,
        panel: this.#panel,
        overlay: this.#overlay,
        panelBody: this.#panelBody,
        closeBtn: this.#closeBtn,
        config: UI_CONFIG,
      });
    }

    this.#closeBtn?.addEventListener('click', () => this.#drawer?.close());
    this.#overlay?.addEventListener('click', () => this.#drawer?.close());
    document.addEventListener('keydown', this.#onKeydown);

    this.#search?.addEventListener('input', () => {
      if (this.#searchDebounce != null) clearTimeout(this.#searchDebounce);
      this.#searchDebounce = setTimeout(() => {
        this.#searchDebounce = null;
        this.#onSearchChange();
      }, SEARCH_DEBOUNCE_MS);
    });

    this.#filtersContainer?.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const chip = target.closest('[data-filter-country]');
      if (!chip || !this.contains(chip) || (chip instanceof HTMLElement && chip.hidden)) return;

      event.preventDefault();
      this.#onCountryClick(chip);
    });

    this.#paginationNav?.querySelector('[data-product-store-finder-pagination-prev]')?.addEventListener('click', () => {
      if (this.#page > 1) {
        this.#scrollAfterPageChange = true;
        this.#page -= 1;
        void this.#loadStores();
      }
    });

    this.#paginationNav?.querySelector('[data-product-store-finder-pagination-next]')?.addEventListener('click', () => {
      const lastPage = this.#lastMeta?.last_page ?? 1;
      if (this.#page < lastPage) {
        this.#scrollAfterPageChange = true;
        this.#page += 1;
        void this.#loadStores();
      }
    });

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    this.#renderCountryFilters();
    this.#updateVisibility();

    if (this.#sku) {
      void this.#loadStores();
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener('keydown', this.#onKeydown);
    if (this.#searchDebounce != null) clearTimeout(this.#searchDebounce);
    this.#fetchAbort?.abort();
    if (this.#drawer?.isOpen() || document.body.dataset.productStoreFinderScrollY != null) {
      this.#drawer?.finishClose();
    }
  }

  /**
   * @param {string} sku
   */
  #setSku(sku) {
    const next = (sku || '').trim();
    if (next === this.#sku) return;

    this.#sku = next;
    this.#page = 1;
    this.#countryCode = 'all';
    this.#searchQuery = '';
    if (this.#search) this.#search.value = '';
    this.#clearSkuCaches();
    this.#renderCountryFilters();
    this.#updateVisibility();

    if (this.#sku) {
      void this.#loadStores();
    } else {
      this.#fetchAbort?.abort();
      this.#drawer?.close();
      this.#grid?.replaceChildren();
      this.#setEmpty(false);
      this.#setError(false);
      this.#applyPagination(null);
    }
  }

  #updateVisibility() {
    const hidden = !this.#sku;
    this.classList.toggle('product-store-finder--hidden', hidden);
  }

  /**
   * @param {boolean} loading
   */
  #setLoading(loading) {
    this.classList.toggle('store-finder--loading', loading);

    if (loading) {
      this.#loadingEl?.removeAttribute('hidden');
      this.#grid?.setAttribute('aria-busy', 'true');
      this.querySelectorAll('[data-filter-country]').forEach((chip) => {
        chip.setAttribute('disabled', '');
        chip.setAttribute('aria-disabled', 'true');
      });
      this.#paginationNav
        ?.querySelectorAll('button')
        .forEach((btn) => btn.setAttribute('disabled', ''));
      this.#search?.setAttribute('disabled', '');
    } else {
      this.#loadingEl?.setAttribute('hidden', '');
      this.#grid?.setAttribute('aria-busy', 'false');
      this.#search?.removeAttribute('disabled');
      this.querySelectorAll('[data-filter-country]').forEach((chip) => {
        chip.removeAttribute('disabled');
        chip.setAttribute('aria-disabled', 'false');
      });
      this.#paginationNav
        ?.querySelectorAll('button')
        .forEach((btn) => btn.removeAttribute('disabled'));
    }
  }

  #getApiUrl() {
    const path = this.dataset.storesApiPath || '/apps/k-design-1/stores-for-item';
    return new URL(path, window.location.origin);
  }

  /**
   * @param {URLSearchParams} params
   */
  #appendSearchParam(params) {
    if (this.#searchQuery) {
      params.set('search', this.#searchQuery);
    }
  }

  /**
   * @param {Element} chip
   */
  #onCountryClick(chip) {
    const code = chip.getAttribute('data-filter-country') || 'all';
    if (code === this.#countryCode) return;

    this.#countryCode = code;
    this.#page = 1;
    this.#clearClientListCache();
    this.#renderCountryFilters();
    void this.#loadStores();
  }

  #onSearchChange() {
    const next = (this.#search?.value || '').trim();
    if (next === this.#searchQuery) return;

    this.#searchQuery = next;
    this.#page = 1;
    this.#clearClientListCache();
    void this.#loadStores();
  }

  #clearClientListCache() {
    this.#clientSearchMode = false;
    this.#clientFilteredCache = null;
    this.#clientCacheKey = '';
  }

  #clearSkuCaches() {
    this.#clearClientListCache();
    this.#allStoresRawCache = null;
    this.#allStoresRawCacheKey = '';
    this.#countryFiltersFromApi = false;
    this.#backendCountries = defaultCountryFilterOptions(this.#countryLabels);
  }

  /**
   * @param {AbortSignal} signal
   */
  async #refineCountryFiltersForSku(signal) {
    if (this.#countryFiltersFromApi || !this.#sku) return;

    const allRaw = await this.#fetchAllStoresForSku(signal);
    if (signal.aborted) return;

    /** @type {Set<string>} */
    const codes = new Set();
    for (const raw of allRaw) {
      const store = normalizeStore(raw, this.#countryLabels, 'Product store finder');
      if (store.countryCode) codes.add(store.countryCode);
    }

    if (!codes.size) return;

    const refined = countryFilterOptionsFromCodes(codes, this.#countryLabels);
    if (!refined.length) return;

    this.#backendCountries = refined;
    this.#renderCountryFilters();

    if (this.#countryCode !== 'all' && !codes.has(this.#countryCode)) {
      this.#countryCode = 'all';
      this.#page = 1;
      this.#clearClientListCache();
      void this.#loadStores();
    }
  }

  /**
   * @param {string} [activeCode]
   */
  #renderCountryFilters(activeCode) {
    if (!this.#filtersContainer || this.dataset.designMode === 'true') return;

    renderCountryFilterChips(
      this.#filtersContainer,
      this.#backendCountries,
      activeCode || this.#countryCode || 'all',
      { all: this.#labelCountryAll },
      {
        chip: 'store-finder__filter',
        active: 'store-finder__filter--active',
      }
    );
  }

  /**
   * @param {Record<string, unknown>} body
   * @returns {boolean}
   */
  #applyCountryFiltersFromBody(body) {
    const { countries, fromApi } = resolveCountryFilters(body, this.#countryLabels);
    this.#backendCountries = countries;
    this.#countryFiltersFromApi = fromApi;
    this.#renderCountryFilters();

    if (
      this.#countryCode !== 'all' &&
      this.#backendCountries.length > 0 &&
      !this.#backendCountries.some((item) => item.code === this.#countryCode)
    ) {
      return false;
    }

    return true;
  }

  /**
   * @param {number} page
   * @param {AbortSignal} signal
   * @param {{ includeCountry?: boolean, includeSearch?: boolean }} [options]
   */
  async #fetchStorePage(page, signal, options = {}) {
    const { includeCountry = true, includeSearch = true } = options;
    const apiUrl = this.#getApiUrl();
    const params = new URLSearchParams();
    params.set('sku', this.#sku);
    if (includeCountry && this.#countryCode && this.#countryCode !== 'all') {
      params.set('countryCode', this.#countryCode);
    }
    if (page > 1) {
      params.set('page', String(page));
    }
    if (includeSearch) {
      this.#appendSearchParam(params);
    }
    apiUrl.search = params.toString();

    const response = await fetch(apiUrl.toString(), {
      signal,
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const body = await response.json();
    const parsed = parseStoreApiResponse(body, page);

    return {
      body,
      data: parsed.data,
      meta: parsed.meta,
    };
  }

  /**
   * @param {AbortSignal} signal
   */
  async #fetchAllStoresForSku(signal) {
    const cacheKey = `${this.#sku}|all-raw`;
    if (this.#allStoresRawCacheKey === cacheKey && this.#allStoresRawCache) {
      return this.#allStoresRawCache;
    }

    const allRaw = [];
    let page = 1;
    let lastPage = 1;
    let perPage = 20;

    do {
      const result = await this.#fetchStorePage(page, signal, {
        includeCountry: false,
        includeSearch: false,
      });
      if (signal.aborted) return [];

      if (page === 1) {
        this.#applyCountryFiltersFromBody(result.body);
      }

      allRaw.push(...result.data);
      lastPage = result.meta.last_page;
      perPage = result.meta.per_page || perPage;
      page += 1;
    } while (page <= lastPage);

    if (signal.aborted) return [];

    this.#allStoresRawCache = allRaw;
    this.#allStoresRawCacheKey = cacheKey;
    this.#clientPerPage = perPage;

    return allRaw;
  }

  /**
   * @param {import('./store-location-data.js').NormalizedStore} store
   */
  #matchesSearchFilter(store) {
    const query = this.#searchQuery.trim().toLowerCase();
    if (!query) return true;

    const haystack = [store.title, store.line1, store.line2, store.countryLabel, store.countryCode]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(query);
  }

  /**
   * @param {unknown[]} rawStores
   */
  #filterRawStoresBySearch(rawStores) {
    if (!this.#searchQuery) return rawStores;

    return rawStores.filter((raw) =>
      this.#matchesSearchFilter(normalizeStore(raw, this.#countryLabels, 'Product store finder'))
    );
  }

  /**
   * @param {unknown[]} rawStores
   */
  #apiRespectsSearchFilter(rawStores) {
    if (!this.#searchQuery) return true;
    if (rawStores.length === 0) return true;

    return rawStores.every((raw) =>
      this.#matchesSearchFilter(normalizeStore(raw, this.#countryLabels, 'Product store finder'))
    );
  }

  /**
   * @param {StoreMeta} meta
   * @param {unknown[]} data
   */
  #applyLoadResult(meta, data) {
    this.#page = meta.current_page;
    this.#lastMeta = meta;
    this.#renderStores(data);
    this.#applyPagination(this.#lastMeta);

    if (data.length === 0) {
      this.#setEmpty(true);
    }
  }

  /**
   * @param {AbortSignal} signal
   */
  async #loadWithClientSearchFilter(signal) {
    this.#clientSearchMode = true;
    const cacheKey = `${this.#sku}|search|${this.#searchQuery}`;

    if (this.#clientCacheKey !== cacheKey || !this.#clientFilteredCache) {
      const allRaw = await this.#fetchAllStoresForSku(signal);
      if (signal.aborted) return;

      this.#clientFilteredCache = this.#filterRawStoresBySearch(allRaw);
      this.#clientCacheKey = cacheKey;
    }

    this.#renderClientSearchPage();
  }

  #renderClientSearchPage() {
    const filtered = this.#clientFilteredCache ?? [];
    const perPage = this.#clientPerPage || 20;
    const total = filtered.length;
    const lastPage = Math.max(1, Math.ceil(total / perPage) || 1);

    if (this.#page > lastPage) {
      this.#page = lastPage;
    }
    if (this.#page < 1) {
      this.#page = 1;
    }

    const start = (this.#page - 1) * perPage;
    const pageSlice = filtered.slice(start, start + perPage);

    this.#applyLoadResult(
      {
        current_page: this.#page,
        last_page: lastPage,
        per_page: perPage,
        total,
      },
      pageSlice
    );
  }

  async #loadStores() {
    if (!this.#sku || !this.#grid) return;

    const scrollAfterLoad = this.#scrollAfterPageChange;
    this.#scrollAfterPageChange = false;

    const searchCacheKey = `${this.#sku}|search|${this.#searchQuery}`;
    if (
      this.#clientSearchMode &&
      this.#searchQuery &&
      this.#clientCacheKey === searchCacheKey &&
      this.#clientFilteredCache
    ) {
      this.#setLoading(true);
      this.#setError(false);
      this.#setEmpty(false);
      this.#renderClientSearchPage();
      this.#setLoading(false);
      if (scrollAfterLoad) {
        this.#scrollToSectionTop();
      }
      return;
    }

    this.#fetchAbort?.abort();
    const controller = new AbortController();
    this.#fetchAbort = controller;

    this.#setLoading(true);
    this.#setError(false);
    this.#setEmpty(false);

    try {
      const result = await this.#fetchStorePage(this.#page, controller.signal);
      if (controller.signal.aborted) return;

      if (!this.#applyCountryFiltersFromBody(result.body)) {
        this.#countryCode = 'all';
        this.#page = 1;
        this.#clearClientListCache();
        void this.#loadStores();
        return;
      }

      if (this.#searchQuery && !this.#apiRespectsSearchFilter(result.data)) {
        await this.#loadWithClientSearchFilter(controller.signal);
        return;
      }

      this.#clearClientListCache();
      this.#applyLoadResult(
        {
          current_page: result.meta.current_page || this.#page,
          last_page: result.meta.last_page,
          per_page: result.meta.per_page || result.data.length || 20,
          total: result.meta.total || result.data.length,
        },
        result.data
      );

      void this.#refineCountryFiltersForSku(controller.signal);
    } catch (err) {
      if (controller.signal.aborted) return;
      console.warn('Product store finder: fetch failed', err);
      this.#clearClientListCache();
      this.#renderStores([]);
      this.#applyPagination(null);
      this.#setError(true);
    } finally {
      if (!controller.signal.aborted) {
        this.#setLoading(false);
        if (scrollAfterLoad) {
          this.#scrollToSectionTop();
        }
      }
    }
  }

  /**
   * @param {boolean} on
   */
  #setError(on) {
    if (!this.#statusEl) return;
    if (on) {
      this.#statusEl.textContent = this.#labelError;
      this.#statusEl.hidden = false;
      this.#statusEl.classList.add('store-finder__status--error');
    } else {
      this.#statusEl.hidden = true;
      this.#statusEl.textContent = '';
      this.#statusEl.classList.remove('store-finder__status--error');
    }
  }

  /**
   * @param {boolean} on
   */
  #setEmpty(on) {
    if (this.#emptyEl) this.#emptyEl.hidden = !on;
  }

  /**
   * @param {StoreMeta | null} meta
   */
  #applyPagination(meta) {
    applyStorePagination(this, meta, {
      nav: this.#paginationNav,
      prevSelector: '[data-product-store-finder-pagination-prev]',
      nextSelector: '[data-product-store-finder-pagination-next]',
      statusSelector: '[data-product-store-finder-pagination-status]',
      rangeTemplate: this.dataset.paginationRangeTemplate || '',
    });
  }

  #scrollToSectionTop() {
    const anchor =
      this.querySelector('.store-finder__title') ??
      this.querySelector('.store-finder.section') ??
      this;
    const top = anchor.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
    });
  }

  /**
   * @param {unknown[]} rawStores
   */
  #renderStores(rawStores) {
    if (!this.#grid || !this.#sectionId) return;

    this.#drawer?.close();

    renderStoreResults(
      this,
      this.#grid,
      this.#sectionId,
      rawStores,
      this.#countryLabels,
      UI_CONFIG,
      {
        showOnMap: this.#labelShowOnMap,
        deliveryHours: this.#labelDeliveryHours,
        openingHours: this.#labelOpeningHours,
      },
      this.#mapIconHtml,
      'Product store finder',
      (index) => this.#drawer?.open(index)
    );
  }
}

if (!customElements.get('product-store-finder')) {
  customElements.define('product-store-finder', ProductStoreFinder);
}
